import MarkdownIt from 'markdown-it';
import moment from 'moment';
import { rgba } from 'polished';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useMarkdownConverter } from '@treams/treams-hooks';
import { PopOutIcon, TreamsLogoIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	AICard,
	Avatar,
	Badge,
	Button,
	FormattedTranscript,
	Heading3,
	IconWrapper,
	MarkdownStyled,
	Paragraph,
	QuillText,
	UserAvatarView,
} from '@treams/treams-ui';

import { AudioPlayer } from '../../Recording/AudioPlayer';
import { estimatePromptCostUsd } from '../../ai-agent/estimateAgentChatCost';
import { targetTypeMapping } from './ChatHistory';

const TranscriptText = styled(Box)`
	line-height: 1.6;
	border-radius: 8px;
	padding: 8px 16px;
	background-color: ${({ theme }) => rgba(theme.colors.primary, 0.02)};
	border: 1px solid ${({ theme }) => rgba(theme.colors.primary, 0.15)};
	font-size: 13px;
	max-height: 500px;
	overflow-y: auto;
`;

type ParsedPromptResponse =
	| { kind: 'json'; data: { insights?: { text: string; explanation: string }[]; summary?: string } }
	| { kind: 'markdown'; text: string };

// Prompt responses are stored in two shapes: streamed prompts persist plain
// markdown as `{ content }`, while non-streaming prompts persist the full
// completion as `{ message: { content } }` (or the legacy `{ choices: [...] }`).
function contentFromPromptResponse(
	response: graphql.PromptHistory['response'] | undefined,
): string | undefined {
	const content =
		response?.content ?? response?.message?.content ?? response?.choices?.[0]?.message?.content;

	return typeof content === 'string' && content.trim() ? content : undefined;
}

function parsePromptResponse(content: string): ParsedPromptResponse {
	try {
		const data = JSON.parse(content);

		if (data && typeof data === 'object' && ('insights' in data || 'summary' in data)) {
			return { kind: 'json', data };
		}
	} catch {
		// Streamed responses are plain markdown, not JSON.
	}

	return { kind: 'markdown', text: content };
}

export default function ChatHistoryDetails() {
	const { t } = useTranslation();
	const { workspaceID, id } = useParams<{ workspaceID: string; id: string }>();
	const { markdownToQuillDelta } = useMarkdownConverter();
	const query = graphql.usePromptHistoryQuery({
		variables: { workspaceID, promptHistoryID: id },
	});

	const promptHistory = useMemo(() => query.data?.promptHistory, [query.data?.promptHistory]);

	const messages = useMemo(() => {
		const messages: { role: string; content: string | null }[] = promptHistory?.messages || [];

		return messages;
	}, [promptHistory?.messages]);

	const estimatedCost = useMemo(() => {
		if (!promptHistory) {
			return undefined;
		}

		return estimatePromptCostUsd({
			model: promptHistory.model,
			inputTokens: promptHistory.inputTokens,
			cachedInputTokens: promptHistory.cachedInputTokens,
			cacheWriteTokens: promptHistory.cacheWriteTokens,
			outputTokens: promptHistory.outputTokens,
		});
	}, [promptHistory]);

	const responseContent = useMemo(
		() => contentFromPromptResponse(promptHistory?.response),
		[promptHistory?.response],
	);

	return (
		<Box flexDirection="column">
			<Box display="flex" flexDirection="row">
				<Box flexDirection="column" flexGrow={1} flexBasis={0}>
					<Heading3 mb={2}>Inzichten gemaakt voor</Heading3>
					<Box display="flex" flexDirection="row" alignItems="center" mb={4}>
						{promptHistory?.user && (
							<Avatar user={promptHistory.user as UserAvatarView} size={32} />
						)}
						<Box ml={2} flexDirection="column" alignItems="start">
							<Paragraph>
								{promptHistory?.user?.firstName} {promptHistory?.user?.lastName}
							</Paragraph>
							<Paragraph small>
								{promptHistory?.workspaceID} - {moment(promptHistory?.createdAt).format('LLL')}
							</Paragraph>
						</Box>
					</Box>
				</Box>
				<Box flexDirection="column" flexGrow={1} flexBasis={0}>
					{promptHistory?.targetDetails?.employee && (
						<>
							<Heading3 mb={2}>{targetTypeMapping[promptHistory?.targetType || '']} over</Heading3>
							<Box display="flex" flexDirection="row" alignItems="center" mb={4}>
								{promptHistory?.user && (
									<Avatar user={promptHistory.targetDetails.employee as UserAvatarView} size={32} />
								)}
								<Box ml={2} flexDirection="column" alignItems="start">
									<Paragraph>
										{promptHistory.targetDetails.employee.firstName}{' '}
										{promptHistory.targetDetails.employee.lastName}
									</Paragraph>
								</Box>
							</Box>
						</>
					)}
				</Box>
			</Box>

			{(promptHistory?.model ||
				promptHistory?.inputTokens != null ||
				promptHistory?.outputTokens != null) && (
				<Box>
					<Heading3>Model usage</Heading3>
					<Paragraph small>
						{promptHistory.model && <>{promptHistory.model} - </>}
						{promptHistory.inputTokens ?? 0} in
						{(promptHistory.cachedInputTokens ?? 0) > 0 && (
							<> ({promptHistory.cachedInputTokens} cached)</>
						)}
						{(promptHistory.cacheWriteTokens ?? 0) > 0 && (
							<> ({promptHistory.cacheWriteTokens} cache write)</>
						)}{' '}
						/ {promptHistory.outputTokens ?? 0} out tokens
					</Paragraph>
					<Flex alignItems="center">
						<IconWrapper mr={1} size={16} color="slateBlue">
							<TreamsLogoIcon />
						</IconWrapper>
						<Paragraph small mb={0}>
							{estimatedCost != null
								? `Estimated cost: ~$${estimatedCost.toFixed(2)}`
								: 'Estimated cost: n/a'}
						</Paragraph>
					</Flex>
				</Box>
			)}

			{promptHistory?.targetID &&
				(promptHistory?.targetType === 'EVALUATION_SUBMISSION' ||
					promptHistory?.targetType === 'CONVERSATION_INSIGHTS_EMPLOYEE' ||
					promptHistory?.targetType === 'CONVERSATION_INSIGHTS_MANAGER') && (
					<Box display="flex" flexDirection="row" alignItems="center" justifyContent="start">
						<Button
							type="secondary"
							icon={<PopOutIcon />}
							small
							title="Ga naar gesprek"
							onClick={() => {
								window.open(
									`https://${promptHistory.workspaceID}.ontreams.com/conversations/${promptHistory.targetID}`,
									'_blank',
								);
							}}
						/>
					</Box>
				)}

			{promptHistory?.targetID && promptHistory?.targetType === 'AI_AGENT' && (
				<Box display="flex" flexDirection="row" alignItems="center" justifyContent="start">
					<Button
						type="secondary"
						icon={<PopOutIcon />}
						small
						title="Ga naar chat"
						onClick={() => {
							window.open(
								`https://${promptHistory.workspaceID}.ontreams.com/assistant/${promptHistory.targetID}`,
								'_blank',
							);
						}}
					/>
				</Box>
			)}

			{promptHistory?.targetType === 'TRANSCRIPTION_SUMMARY' &&
				promptHistory.target?.__typename === 'Transcription' &&
				(() => {
					const transcription = promptHistory.target;
					const isNote = transcription.targetType === graphql.TranscriptionTargetType.Note;
					const path = isNote ? 'notes' : 'conversations';

					return (
						<>
							<Box display="flex" flexDirection="row" alignItems="center" justifyContent="start">
								<Button
									type="secondary"
									icon={<PopOutIcon />}
									small
									title={isNote ? 'Ga naar notitie' : 'Ga naar gesprek'}
									onClick={() => {
										window.open(
											`https://${promptHistory.workspaceID}.ontreams.com/${path}/${transcription.targetID}`,
											'_blank',
										);
									}}
								/>
							</Box>

							{transcription.audioUrl && (
								<Box mt={4}>
									<Heading3 mb={2}>Audio</Heading3>
									<AudioPlayer src={transcription.audioUrl} />
								</Box>
							)}

							{transcription.transcript && (
								<Box mt={4}>
									<Heading3 mb={2}>Transcriptie</Heading3>
									<TranscriptText>
										<FormattedTranscript text={transcription.transcript} />
									</TranscriptText>
								</Box>
							)}
						</>
					);
				})()}

			{promptHistory?.featureRating && (
				<>
					<Heading3 mt={4}>Feedback</Heading3>
					<Box display="flex" flexDirection="row" alignItems="start" padding={2}>
						<Box mt={1.5} mr={2}>
							<Badge color={promptHistory.featureRating.binaryValue ? 'green' : 'red'} />
						</Box>
						<Paragraph style={{ whiteSpace: 'pre-wrap' }}>
							{promptHistory.featureRating.text}
						</Paragraph>
					</Box>
				</>
			)}
			{promptHistory && responseContent && (
				<Box lineHeight="1.5">
					<Heading3 mt={4} mb={3}>
						{t('settings.ai-workbench.chat-history.response')}
					</Heading3>
					<ResponseBox targetType={promptHistory.targetType} content={responseContent} />
				</Box>
			)}
			{messages.length > 0 && (
				<>
					<Heading3 mt={4}>Prompt</Heading3>
					{messages.map(
						(message, index) =>
							message.content && (
								<Box key={index} flexDirection="column" mb={3}>
									<Paragraph fontWeight="bold" fontStyle="italic" small mb={1}>
										{message.role}
									</Paragraph>
									<Box mb={3} backgroundColor="#eee" padding={4}>
										<QuillText value={markdownToQuillDelta(message.content)} />
									</Box>
								</Box>
							),
					)}
				</>
			)}
		</Box>
	);
}

const INSIGHTS_TARGET_TYPES = [
	'EVALUATION_SUBMISSION',
	'CONVERSATION_INSIGHTS_EMPLOYEE',
	'CONVERSATION_INSIGHTS_MANAGER',
];

function markdownCardTitle(targetType: string): string {
	if (targetType === 'AI_AGENT') return 'Antwoord';
	if (INSIGHTS_TARGET_TYPES.includes(targetType)) return 'Inzichten';
	if (targetType === 'NOTE_SUMMARIES' || targetType === 'TRANSCRIPTION_SUMMARY') {
		return 'Samenvatting';
	}
	if (targetType === 'TALKING_POINTS') return 'Talking points';
	if (targetType === 'TALENT_INSIGHTS') return 'Talent Insights';

	return 'Response';
}

function ResponseBox({ targetType, content }: { targetType: string; content: string }) {
	const { markdownToQuillDelta } = useMarkdownConverter();
	const md = useMemo(() => new MarkdownIt(), []);
	const parsed = useMemo(() => parsePromptResponse(content), [content]);

	if (parsed.kind === 'json') {
		if (INSIGHTS_TARGET_TYPES.includes(targetType) && parsed.data.insights) {
			const insights = parsed.data.insights;

			return (
				<AICard title="Inzichten">
					{insights.map((insight, index) => (
						<Fragment key={index}>
							<b>{insight.text}</b>
							<br />
							{insight.explanation}
							{index < insights.length - 1 && (
								<>
									<br />
									<br />
								</>
							)}
						</Fragment>
					))}
				</AICard>
			);
		}

		if (targetType === 'TRANSCRIPTION_SUMMARY' && parsed.data.summary) {
			return (
				<AICard title="Samenvatting">
					<QuillText value={markdownToQuillDelta(parsed.data.summary)} />
				</AICard>
			);
		}

		return (
			<pre
				style={{
					whiteSpace: 'pre-wrap',
					lineHeight: '2',
					fontFamily: 'Source Sans Pro',
					backgroundColor: '#eee',
					padding: '16px',
					borderRadius: '8px',
				}}
			>
				{JSON.stringify(parsed.data, null, 2)}
			</pre>
		);
	}

	return (
		<AICard title={markdownCardTitle(targetType)}>
			<MarkdownStyled dangerouslySetInnerHTML={{ __html: md.render(parsed.text) }} />
		</AICard>
	);
}
